import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { getSpecialtyIcon } from '../lib/specialtyIcons';
import { Menu, Transition } from '@headlessui/react';
import { CreditCard, Mail, LogOut, MessageCircleQuestion, Clock, Trash2, Sparkles, FileText, ChevronDown, BookOpen, GraduationCap, ScrollText, Database, UserRound, UserCog, CheckCircle, MenuIcon, X, Key, AlertCircle } from 'lucide-react';
import { 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  updateDoc, 
  increment, 
  onSnapshot,
  query,
  where,
  orderBy,
  getDocs
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useNavigate, Link } from 'react-router-dom';
import { sendErrorNotification } from '../utils/email';
import { trackEvent, AnalyticsEvents } from '../lib/analytics';
import { trackClarityEvent } from '../lib/clarity';
import { ChatSidebar } from './ChatSidebar';

// Simple debounce implementation to avoid lodash dependency
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

interface Consultation {
  specialty: string;
  response: string;
}

interface SpecialtyStatus {
  specialty: string;
  status: 'pending' | 'consulting' | 'completed';
}

// Define user profile types
type ProfileType = 'patient' | 'professional';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  consultations?: Consultation[];
  stage?: string;
  processingStage?: string;
  specialtyStatuses?: SpecialtyStatus[];
  error?: boolean;
  isStreaming?: boolean;
  isConsulting?: boolean;
  isNonHealth?: boolean;
  model?: 'mini' | 'pro';
  conversationId: string;
  userId: string;
  timestamp: Date;
  feedback?: MessageFeedback;
}

interface ConsultationPopupProps {
  consultation: Consultation | null;
  onClose: () => void;
}

const ConsultationPopup: React.FC<ConsultationPopupProps> = ({ consultation, onClose }) => {
  if (!consultation) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        transition={{ duration: 0.1 }}
        className="bg-[#1A1B1E] rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto border border-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#1A1B1E] border-b border-gray-800 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getSpecialtyIcon(consultation.specialty)}
            <h2 className="text-gray-100 text-lg font-medium">{consultation.specialty} AI - Consultation Report</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6 p-4 bg-gradient-to-r from-[#1d3d39] to-[#1A1B1E] rounded-lg border border-[#1a3733]">
            <div className="flex items-start gap-3">
              <div className="mt-1">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-[#00B5A1]">
                  <path fillRule="evenodd" d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813A3.75 3.75 0 007.466 7.89l.813-2.846A.75.75 0 019 4.5z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-100">Evidence-Based Medical AI</h4>
                <div className="flex flex-wrap gap-2 mt-2">
                  <div className="flex items-center gap-2 px-2.5 py-1.5 bg-[#232429] rounded-md border border-gray-700">
                    <FileText className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-xs font-medium text-gray-300">Articles</span>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 py-1.5 bg-[#232429] rounded-md border border-gray-700">
                    <BookOpen className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-xs font-medium text-gray-300">Textbooks</span>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 py-1.5 bg-[#232429] rounded-md border border-gray-700">
                    <ScrollText className="w-3.5 h-3.5 text-purple-400" />
                    <span className="text-xs font-medium text-gray-300">Reviews</span>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 py-1.5 bg-[#232429] rounded-md border border-gray-700">
                    <GraduationCap className="w-3.5 h-3.5 text-orange-400" />
                    <span className="text-xs font-medium text-gray-300">Meta-analysis</span>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 py-1.5 bg-[#232429] rounded-md border border-gray-700">
                    <FileText className="w-3.5 h-3.5 text-indigo-400" />
                    <span className="text-xs font-medium text-gray-300">Guidelines</span>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  Our AI analyzes multiple medical databases to provide evidence-based information
                </div>
              </div>
            </div>
          </div>

          <div className="prose prose-sm max-w-none mb-6 prose-invert">
            <div className="text-[15px] leading-relaxed text-gray-300">
              <ReactMarkdown
                components={{
                  h1: ({node, ...props}) => <h1 className="text-2xl font-bold mb-4 mt-6 text-gray-100" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-xl font-bold mb-3 mt-5 text-gray-100" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-lg font-bold mb-2 mt-4 text-gray-100" {...props} />,
                  h4: ({node, ...props}) => <h4 className="text-base font-bold mb-2 mt-3 text-gray-100" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-gray-100" {...props} />,
                  p: ({node, ...props}) => <p className="mb-4 text-gray-300" {...props} />,
                  ul: ({node, ...props}) => {
                    // Check if the parent is a heading
                    const isHeadingChild = node && 'parent' in node && (node as MarkdownNode).parent?.type === 'heading';
                    return (
                      <ul className={`${isHeadingChild ? '' : 'list-disc'} pl-6 mb-4 space-y-2`} {...props} />
                    );
                  },
                  ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
                  li: ({node, ...props}) => <li className="text-gray-300" {...props} />,
                }}
              >
                {consultation.response}
              </ReactMarkdown>
            </div>
          </div>

          <div className="pt-6 border-t border-gray-800 text-xs text-gray-500 leading-relaxed">
            This consultation report is for informational purposes only and should not be considered as medical advice. While our AI system uses evidence-based medical databases, each patient's situation is unique. Always consult with a qualified healthcare professional for personalized medical advice, diagnosis, or treatment. In case of emergency, contact your local emergency services immediately.
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Add this type definition near other interfaces at the top
interface MessageFeedback {
  rating: 'like' | 'dislike' | null;
  message: string;
}

interface MarkdownNode {
  parent?: {
    type?: string;
  };
}

// Add this component before the Chat component
interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (message: string) => void;
  rating: 'like' | 'dislike';
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, onSubmit, rating }) => {
  const [feedbackMessage, setFeedbackMessage] = useState('');
  
  if (!isOpen) return null;
  
  const handleSubmit = () => {
    onSubmit(feedbackMessage);
    setFeedbackMessage('');
    onClose();
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#1A1B1E] rounded-lg p-6 w-full max-w-md border border-gray-800" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-3 text-gray-100">
          {rating === 'like' ? 'Thanks for your feedback!' : 'We\'re sorry to hear that'}
        </h3>
        <p className="mb-4 text-gray-300 text-sm">
          {rating === 'like' 
            ? 'What did you find helpful about this response?' 
            : 'What could we improve about this response?'}
        </p>
        <textarea
          className="w-full border border-gray-700 rounded-lg p-3 mb-4 h-32 bg-[#232429] text-gray-100 focus:border-[#00B5A1] focus:ring-1 focus:ring-[#00B5A1] focus:outline-none text-sm"
          placeholder="Your feedback (optional)"
          value={feedbackMessage}
          onChange={e => setFeedbackMessage(e.target.value)}
        />
        <div className="flex justify-end gap-3">
          <button
            className="px-4 py-2 text-gray-400 hover:text-gray-200 text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-[#00B5A1] hover:bg-[#00a090] text-white rounded-md text-sm"
            onClick={handleSubmit}
          >
            Submit feedback
          </button>
        </div>
      </div>
    </div>
  );
};

interface MessageFeedbackProps {
  messageId: string;
  onSubmitFeedback: (messageId: string, feedback: MessageFeedback) => void;
  setFeedbackState: React.Dispatch<React.SetStateAction<{
    showModal: boolean;
    messageId: string;
    rating: 'like' | 'dislike';
    handleSubmit: (message: string) => void;
  }>>;
}

const MessageFeedbackComponent: React.FC<MessageFeedbackProps> = ({ messageId, onSubmitFeedback, setFeedbackState }) => {
  const [rating, setRating] = useState<'like' | 'dislike' | null>(null);
  
  const handleRatingClick = (newRating: 'like' | 'dislike') => {
    setRating(newRating);
    setFeedbackState({
      showModal: true,
      messageId,
      rating: newRating,
      handleSubmit: (message: string) => {
        onSubmitFeedback(messageId, {
          rating: newRating,
          message
        });
      }
    });
  };
  
  return (
    <div className="flex items-center gap-3">
      <button 
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-colors ${
          rating === 'like' 
            ? 'bg-[#1d3d39] text-[#00B5A1]' 
            : 'text-gray-400 hover:text-gray-300 hover:bg-[#252525]'
        }`}
        onClick={() => handleRatingClick('like')}
        aria-label="Helpful"
        title="Helpful"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 11v11M4 15v3a4 4 0 004 4h8.5a6 6 0 005.5-3.6l2.5-5.7A4 4 0 0021 8.7l-.7-1.7A1 1 0 0019.4 6H15a1 1 0 01-1-1V3a2 2 0 00-4 0v6l-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="text-xs font-medium">Helpful</span>
      </button>
      <button 
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-colors ${
          rating === 'dislike' 
            ? 'bg-[#1d3d39] text-[#00B5A1]' 
            : 'text-gray-400 hover:text-gray-300 hover:bg-[#252525]'
        }`}
        onClick={() => handleRatingClick('dislike')}
        aria-label="Not helpful"
        title="Not helpful"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 13V2M20 9V6a4 4 0 00-4-4H7.5a6 6 0 00-5.5 3.6L-.5 11.3A4 4 0 003 15.3l.7 1.7a1 1 0 00.9.9H9a1 1 0 011 1v2a2 2 0 004 0v-6l3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="text-xs font-medium">Not helpful</span>
      </button>
    </div>
  );
};

export const Chat: React.FC = () => {
  const { user, logout } = useAuth();
  const { 
    currentConversationId, 
    createNewConversation,
    updateConversationTitle 
  } = useChat();
  
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesRef = useRef<Message[]>([]);
  
  const [showSidebar, setShowSidebar] = useState(window.innerWidth >= 768);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [activeConsultation, setActiveConsultation] = useState<Consultation | null>(null);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);
  const [inputHeight, setInputHeight] = useState('auto');
  const [showCreditUpdatePopup, setShowCreditUpdatePopup] = useState(false);
  const previousCreditsRef = useRef<number>(0);
  const [isNewUser, setIsNewUser] = useState(true);
  const navigate = useNavigate();
  const [selectedModel, setSelectedModel] = useState<'mini' | 'pro'>('mini');
  const [profileType, setProfileType] = useState<ProfileType>('patient');
  const [showProfileSelector, setShowProfileSelector] = useState(false);
  
  // Add feedbackState
  const [feedbackState, setFeedbackState] = useState<{
    showModal: boolean;
    messageId: string;
    rating: 'like' | 'dislike';
    handleSubmit: (message: string) => void;
  }>({
    showModal: false,
    messageId: '',
    rating: 'like',
    handleSubmit: () => {}
  });
  
  const MAX_STORED_MESSAGES = 50;
  const DEBOUNCE_MS = 500;
  
  // Track component mount
  useEffect(() => {
    trackClarityEvent('chat_page_loaded');
  }, []);
  
  // Handle window resize for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      setShowSidebar(window.innerWidth >= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Load messages for current conversation
  useEffect(() => {
    const loadMessagesForConversation = async () => {
      if (!user?.uid || !currentConversationId) {
        setMessages([]);
        return;
      }
      
      try {
        const messagesRef = collection(db, 'messages');
        const q = query(
          messagesRef,
          where('userId', '==', user.uid),
          where('conversationId', '==', currentConversationId),
          orderBy('timestamp', 'asc')
        );
        
        const querySnapshot = await getDocs(q);
        const loadedMessages: Message[] = [];
        
        querySnapshot.forEach((doc) => {
          loadedMessages.push(doc.data() as Message);
        });
        
        setMessages(loadedMessages);
        
        // Update conversation title if it's the default and we have user messages
        const userMessages = loadedMessages.filter(m => m.role === 'user');
        if (userMessages.length > 0) {
          const firstUserMessage = userMessages[0].content;
          const title = firstUserMessage.slice(0, 30) + (firstUserMessage.length > 30 ? '...' : '');
          // Only update if the title is still "New Conversation"
          const convDoc = await getDoc(doc(db, 'conversations', currentConversationId));
          if (convDoc.exists() && convDoc.data().title === 'New Conversation') {
            updateConversationTitle(currentConversationId, title);
          }
        }
        
        // Never show welcome banner regardless of messages
        // setShowWelcomeBanner(loadedMessages.length === 0);
      } catch (error) {
        console.error('Error loading messages:', error);
      }
    };
    
    loadMessagesForConversation();
  }, [user?.uid, currentConversationId, updateConversationTitle]);

  // Update messagesRef whenever messages change
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  
  // Update credit display to show cost per consultation
  const getCreditCost = () => selectedModel === 'pro' ? 15 : 1;

  const handleModelSelect = (e: React.MouseEvent, model: 'mini' | 'pro') => {
    e.preventDefault();
    setSelectedModel(model);
    trackClarityEvent('model_changed', { model_type: model });
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.uid) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          // Fix the toDate error by safely handling different timestamp formats
          let createdAt;
          const createdAtData = userDoc.data().createdAt;
          
          if (createdAtData && typeof createdAtData.toDate === 'function') {
            // It's a Firestore timestamp
            createdAt = createdAtData.toDate();
          } else if (createdAtData instanceof Date) {
            // It's already a Date object
            createdAt = createdAtData;
          } else if (createdAtData) {
            // It might be a timestamp number or string
            createdAt = new Date(createdAtData);
          } else {
            // Fallback
            createdAt = new Date();
          }
          
          const isNewAccount = (new Date().getTime() - createdAt.getTime()) < 5000; // 5 seconds threshold
          setIsNewUser(isNewAccount);
          
          // Get user profile type if it exists
          const userProfileType = userDoc.data().profileType as ProfileType;
          const hasSetProfile = userDoc.data().hasSetProfile === true;
          
          if (userProfileType && hasSetProfile) {
            // User has already set a profile type
            setProfileType(userProfileType);
            setShowProfileSelector(false);
          } else {
            // User hasn't set a profile yet - default to patient but force selection
            setProfileType('patient');
            // Always show profile selector for new users or those who haven't set a profile
            setShowProfileSelector(true);
          }
          
          // Update user document with hasSeenCreditPopup field if it doesn't exist
          if (!userDoc.data().hasSeenCreditPopup) {
            await updateDoc(doc(db, 'users', user.uid), {
              hasSeenCreditPopup: false
            });
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchUserData();
  }, [user?.uid]);

  useEffect(() => {
    const fetchCredits = async () => {
      if (!user?.uid) return;
      try {
        const userDoc = await doc(db, 'users', user.uid);
        const unsubscribe = onSnapshot(userDoc, async (doc) => {
          if (doc.exists()) {
            const newCredits = doc.data().credits || 0;
            const hasSeenCreditPopup = doc.data().hasSeenCreditPopup || false;
            
            setCredits(newCredits);
            
            if (newCredits > previousCreditsRef.current && !isNewUser && !hasSeenCreditPopup) {
              setShowCreditUpdatePopup(true);
              // Update Firebase to mark that user has seen the popup
              await updateDoc(userDoc, {
                hasSeenCreditPopup: true
              });
            }
            previousCreditsRef.current = newCredits;
          }
        });
        return unsubscribe;
      } catch (error) {
        console.error('Error fetching credits:', error);
      }
    };
    fetchCredits();
  }, [user?.uid, isNewUser]);

  useEffect(() => {
    if (user?.uid) {
      // Limit stored messages to prevent localStorage from growing too large
      const messagesToStore = messages.slice(-MAX_STORED_MESSAGES);
      localStorage.setItem(`chat_history_${user.uid}`, JSON.stringify(messagesToStore));
    }
  }, [messages, user?.uid]);

  // Modify the scrollToBottom function to always position the user message at the top
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: behavior,
        block: 'start',  // This positions the element at the top of the viewport
      });
    }
  }, []);

  useEffect(() => {
    // Always scroll user messages to top for better visibility of processing
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      if (latestMessage.role === 'user') {
        // Force immediate scroll for user messages
        scrollToBottom('auto');
      } else {
        // Use smooth scrolling for assistant messages
        scrollToBottom('smooth');
      }
    }
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const checkBusyDepartments = () => {
      // Remove setBusyDepartments functionality since the state was removed
      // Just leave an empty function or implement alternative logic if needed
    };
    checkBusyDepartments();
  }, [messages]);

  const adjustInputHeight = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const lineHeight = 24; // Approximate line height in pixels
    const maxLines = 5; // Allow up to 5 rows before scrolling
    const maxHeight = lineHeight * maxLines;
    
    e.target.style.height = 'auto';
    const scrollHeight = Math.min(e.target.scrollHeight, maxHeight);
    e.target.style.height = `${scrollHeight}px`;
    setInputHeight(`${scrollHeight}px`);
  };

  const updateUserCredits = async (decrease: boolean = true) => {
    if (!user?.uid) return;
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        credits: increment(decrease ? -getCreditCost() : 0)
      });
    } catch (error) {
      console.error('Error updating credits:', error);
    }
  };

  // Function to update user profile type
  const updateUserProfile = async (type: ProfileType) => {
    if (!user?.uid) return;
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        profileType: type,
        hasSetProfile: true
      });
      setProfileType(type);
      setShowProfileSelector(false);
      // Track profile selection
      trackEvent(AnalyticsEvents.PROFILE_SELECTED, { profile_type: type });
      trackClarityEvent('profile_selected', { profile_type: type });
    } catch (error) {
      console.error('Error updating profile type:', error);
    }
  };

  // Function to get the last 3 message exchanges for conversation history
  const getConversationHistory = () => {
    // Filter out processing messages, error messages, etc.
    const relevantMessages = messages.filter(msg => 
      !msg.isConsulting && 
      !msg.error && 
      (msg.role === 'user' || (msg.role === 'assistant' && !msg.isStreaming))
    );
    
    // Get the last 6 messages (3 exchanges) or fewer if not available
    const lastMessages = relevantMessages.slice(-6);
    
    // Format the messages for the API
    return lastMessages.map(msg => ({
      role: msg.role,
      content: msg.content
    }));
  };

  // Update submit function to store messages in Firestore
  const debouncedSubmit = useCallback(
    debounce(async (e: React.FormEvent) => {
      e.preventDefault();
      if (!input.trim() || isLoading) return;
      
      // Check if user has enough credits before proceeding
      if (credits === 0) {
        // Show a toast notification
        const toastId = `no-credits-${Date.now()}`;
        const toastElement = document.createElement('div');
        toastElement.id = toastId;
        toastElement.className = 'fixed top-4 right-4 z-50 bg-red-500 text-white px-4 py-3 rounded shadow-lg flex items-center justify-between transform transition-transform duration-300 ease-in-out';
        toastElement.innerHTML = `
          <div class="flex items-center">
            <div class="mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div>You have 0 credits remaining. Please purchase more credits to continue.</div>
          </div>
          <button class="ml-4 text-white hover:text-gray-200">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        `;
        document.body.appendChild(toastElement);
        
        // Add event listener to close button
        const closeButton = toastElement.querySelector('button');
        if (closeButton) {
          closeButton.addEventListener('click', () => {
            toastElement.classList.add('translate-x-full');
            setTimeout(() => {
              document.body.removeChild(toastElement);
            }, 300);
          });
        }
        
        // Auto-remove toast after 5 seconds
        setTimeout(() => {
          if (document.body.contains(toastElement)) {
            toastElement.classList.add('translate-x-full');
            setTimeout(() => {
              if (document.body.contains(toastElement)) {
                document.body.removeChild(toastElement);
              }
            }, 300);
          }
        }, 5000);
        
        // Redirect to pricing page after a short delay
        setTimeout(() => {
          navigate('/pricing');
        }, 1000);
        return;
      }
      
      // Track message submission in both GA and Clarity
      trackEvent(AnalyticsEvents.MESSAGE_SUBMIT, {
        message_length: input.trim().length,
        model_type: selectedModel,
        profile_type: profileType
      });
      trackClarityEvent('message_submit', {
        message_length: input.trim().length.toString(),
        model_type: selectedModel,
        profile_type: profileType
      });
      
      // Check internet connection
      if (!navigator.onLine) {
        const errorMessage: Message = {
          id: Date.now().toString(),
          content: 'Please check your internet connection and try again.',
          role: 'assistant',
          stage: 'Error',
          error: true,
          consultations: [],
          specialtyStatuses: [],
          conversationId: currentConversationId || Date.now().toString(),
          userId: user?.uid || '',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
        await sendErrorNotification('Internet connection error', user?.uid);
        return;
      }

      setShowWelcomeBanner(false);
      setInputHeight('56px');
      
      // Create a new conversation if none exists
      let activeConversationId = currentConversationId;
      if (!activeConversationId) {
        activeConversationId = await createNewConversation();
      }
      
      const userMessageContent = input.trim(); // Store user message content
      const userMessageId = crypto.randomUUID();
      const responseId = crypto.randomUUID();
      
      const userMessage: Message = {
        id: userMessageId,
        content: userMessageContent,
        role: 'user',
        conversationId: activeConversationId,
        userId: user?.uid || '',
        timestamp: new Date()
      };

      // Save user message to Firestore
      try {
        await addDoc(collection(db, 'messages'), {
          ...userMessage,
          userId: user?.uid,
          userEmail: user?.email || '', // Add user email to stored messages
          timestamp: new Date(),
          model: selectedModel, // Add model type to user message
          profileType: profileType // Add profile type to user message
        });
      } catch (error) {
        console.error('Error saving user message:', error);
      }

      setMessages(prev => [...prev, userMessage]);
      setInput('');
      setIsLoading(true);

      try {
        const consultingId = crypto.randomUUID();
        const consultingMessage: Message = {
          id: consultingId,
          content: 'Processing your question...',
          role: 'assistant',
          isConsulting: true,
          stage: 'Initial Analysis',
          specialtyStatuses: [],
          conversationId: activeConversationId,
          userId: user?.uid || '',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, consultingMessage]);

        // Add timeout to API calls
        const timeoutId = setTimeout(() => {
          if (!hasCompletedResponse) {
            throw new Error('Request timed out after 30 seconds');
          }
        }, 30000);

        // Get conversation history
        const conversationHistory = getConversationHistory();
        
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/consultation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            'Origin': window.location.origin,
            'X-API-Key': import.meta.env.VITE_YESIL_API_SECRET_KEY
          },
          mode: 'cors',
          body: JSON.stringify({
            question: userMessageContent,
            api_model: selectedModel === 'pro' ? 'yesil-o1-pro' : 'yesil-o1-mini',
            profile_type: profileType, // Include profile type in the request
            conversation_history: conversationHistory.length > 0 ? conversationHistory : undefined
          })
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('API Error Details:', {
            status: response.status,
            statusText: response.statusText,
            error: errorText,
          });
          throw new Error(`API request failed: ${response.status} ${response.statusText}\n${errorText}`);
        }

        const assistantMessage: Message = {
          id: responseId,
          content: '',
          role: 'assistant',
          isStreaming: true,
          consultations: [],
          specialtyStatuses: [],
          model: selectedModel,
          conversationId: activeConversationId,
          userId: user?.uid || '',
          timestamp: new Date()
        };
        
        setMessages(prev => prev.filter(msg => msg.id !== consultingId).concat(assistantMessage));

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No reader available');

        let buffer = '';
        let currentSpecialty = '';
        let consultationText = '';
        let finalResponseText = '';
        let hasCompletedResponse = false;
        let isHealthQuestion = true; // Flag to track if it's a health question
        let hasDeductedCredits = false; // Flag to ensure we only deduct once

        // Add immediate processing message
        updateMessage(responseId, {
          processingStage: 'Starting analysis...',
          stage: 'Initial Analysis'
        });

        const textDecoder = new TextDecoder();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = textDecoder.decode(value, { stream: true });
          buffer += chunk;
          
          // Process complete lines
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine) continue;

            // Process status updates
            if (trimmedLine.startsWith('STATUS:')) {
              const status = trimmedLine.split('STATUS:')[1];
              
              switch (status) {
                case 'PROCESSING_STARTED':
                  updateMessage(responseId, {
                    processingStage: 'Starting analysis...',
                    stage: 'Initial Analysis'
                  });
                  break;

                case 'NON_HEALTH':
                  console.log('Received NON_HEALTH status');
                  isHealthQuestion = false;
                  // Wait for the FINAL_RESPONSE to come through
                  // We'll mark the message with a non-health badge but won't override its content
                  updateMessage(responseId, {
                    stage: 'Not Health Related',
                    processingStage: 'Not Health Related',
                    isNonHealth: true, // Flag for the badge
                    isStreaming: true
                  });
                  break;

                case 'HEALTH_QUESTION_CONFIRMED':
                  console.log('Health question confirmed - deducting credits');
                  await updateUserCredits(true);
                  updateMessage(responseId, {
                    processingStage: 'Health question confirmed, analyzing...',
                    stage: 'Health Analysis'
                  });
                  break;

                case 'GENERATING_FINAL':
                  // Fallback credit deduction if HEALTH_QUESTION_CONFIRMED wasn't received
                  if (isHealthQuestion && !hasDeductedCredits) {
                    console.log('Fallback credit deduction at GENERATING_FINAL stage');
                    await updateUserCredits(true);
                    hasDeductedCredits = true;
                  }
                  updateMessage(responseId, {
                    processingStage: 'Generating final response...',
                    stage: 'Final Analysis'
                  });
                  break;

                case 'COMPLETED':
                  hasCompletedResponse = true;
                  
                  console.log('COMPLETED: Before message update');
                  console.log('Current messages state:', messagesRef.current);
                  
                  // Get current message from the ref
                  const currentMessage = messagesRef.current.find(m => m.id === responseId);
                  console.log('Found current message:', currentMessage);
                  
                  // Only proceed with final response update if it's a health question
                  if (isHealthQuestion) {
                    console.log('Current consultations:', currentMessage?.consultations);
                    console.log('Current specialtyStatuses:', currentMessage?.specialtyStatuses);
                    
                    // Create complete update data, preserving existing arrays
                    const updatedMessageData = {
                      content: finalResponseText,
                      stage: 'Consultation completed',
                      processingStage: undefined,
                      isStreaming: false,
                      // Explicitly preserve existing arrays
                      consultations: currentMessage?.consultations || [],
                      specialtyStatuses: currentMessage?.specialtyStatuses || [],
                      model: currentMessage?.model,
                      role: 'assistant' as const,
                      conversationId: activeConversationId,
                      userId: user?.uid || '',
                      timestamp: new Date()
                    };
                    
                    console.log('Updating message with data:', updatedMessageData);
                    updateMessage(responseId, updatedMessageData);
                  }
                  
                  try {
                    // Create complete document for Firestore
                    const firestoreDoc = {
                      id: responseId,
                      messageId: responseId,
                      content: isHealthQuestion ? finalResponseText : currentMessage?.content || '',
                      role: 'assistant',
                      userId: user?.uid || '',
                      userEmail: user?.email || '',
                      model: selectedModel,
                      profileType: profileType,
                      stage: isHealthQuestion ? 'Consultation completed' : 'Not Health Related',
                      consultations: currentMessage?.consultations || [],
                      specialtyStatuses: currentMessage?.specialtyStatuses || [],
                      conversationId: activeConversationId,
                      timestamp: new Date(),
                      userMessageId: userMessageId, // Add reference to the user's message
                      userMessage: userMessageContent // Store the user's original query
                    };
                    
                    console.log("SAVING TO FIRESTORE:", firestoreDoc);
                    
                    // Save to Firestore
                    const messagesCollection = collection(db, 'messages');
                    addDoc(messagesCollection, firestoreDoc)
                      .then(docRef => {
                        console.log("Document saved with ID:", docRef.id);
                      })
                      .catch(error => {
                        console.error("Error saving document:", error);
                      });
                  } catch (error) {
                    console.error("Error in Firestore save:", error);
                  }
                  break;

                case 'ERROR':
                  if (isHealthQuestion) {
                    // Refund credits if there was an error and credits were deducted
                    await updateUserCredits(false);
                  }
                  throw new Error('API encountered an error during processing');
              }

              // Handle specialty-specific statuses
              if (status.startsWith('LOADING_RAG:')) {
                const specialty = status.split('LOADING_RAG:')[1];
                console.log(`Loading RAG for specialty: ${specialty}`);
                updateMessage(responseId, {
                  processingStage: `Loading ${specialty} knowledge base...`
                });
                // Update this specialty to consulting status
                processSpecialtyStatus(responseId, specialty, 'consulting');
              }
              else if (status.startsWith('CONSULTING:')) {
                const specialty = status.split('CONSULTING:')[1];
                console.log(`Consulting specialty: ${specialty}`);
                updateMessage(responseId, {
                  processingStage: `Getting consultation from ${specialty}...`
                });
              }
              else if (status.startsWith('SPECIALTY_COMPLETE:')) {
                const specialty = status.split('SPECIALTY_COMPLETE:')[1];
                console.log(`Specialty complete: ${specialty}`);
                
                if (currentSpecialty === specialty && consultationText) {
                  // Update the consultation with the final text
                  const currentMsg = messagesRef.current.find(m => m.id === responseId);
                  const currentConsultations = currentMsg?.consultations || [];
                  
                  const updatedConsultations = [
                    ...currentConsultations.filter(c => c.specialty !== specialty),
                    { specialty, response: consultationText }
                  ];
                  
                  updateMessage(responseId, {
                    consultations: updatedConsultations
                  });
                  
                  consultationText = '';
                }
                
                // Mark this specialty as completed
                processSpecialtyStatus(responseId, specialty, 'completed');
              }
              continue;
            }

            // Process specialties list
            if (trimmedLine.startsWith('SPECIALTIES:')) {
              const specialtiesText = trimmedLine.split('SPECIALTIES:')[1];
              
              // If we detect specialties, it's a health question - deduct credits if not already done
              if (!hasDeductedCredits && isHealthQuestion) {
                console.log('Specialties detected - deducting credits');
                await updateUserCredits(true);
                hasDeductedCredits = true;
              }
              
              processSpecialties(responseId, specialtiesText);
              continue;
            }

            // Process consultation content
            if (trimmedLine.startsWith('CONSULTATION:')) {
              try {
                const [specialty, ...contentParts] = trimmedLine.split('CONSULTATION:')[1].split(':');
                currentSpecialty = specialty.trim();
                consultationText = contentParts.join(':').trim();
                
                console.log(`Received consultation for ${currentSpecialty}:`, consultationText);
                
                // Get current message state
                const currentMsg = messagesRef.current.find(m => m.id === responseId);
                if (!currentMsg) {
                  console.warn('Message not found for consultation update');
                  continue;
                }
                
                // Create updated consultations array
                const currentConsultations = currentMsg.consultations || [];
                const updatedConsultations = [
                  ...currentConsultations.filter(c => c.specialty !== currentSpecialty),
                  { specialty: currentSpecialty, response: consultationText }
                ];
                
                // Update message with new consultations
                updateMessage(responseId, {
                  consultations: updatedConsultations,
                  specialtyStatuses: currentMsg.specialtyStatuses || []
                });
                
                console.log("Updated consultations:", updatedConsultations);
              } catch (error) {
                console.error("Error processing consultation:", error);
              }
              continue;
            }

            // Process final response
            if (trimmedLine.startsWith('FINAL_RESPONSE:')) {
              finalResponseText = trimmedLine.split('FINAL_RESPONSE:')[1].trim();
              
              // Get current message state
              const currentMsg = messagesRef.current.find(m => m.id === responseId);
              if (!currentMsg) {
                console.warn('Message not found for final response update');
                continue;
              }
              
              // Create final message with preserved consultations and specialty statuses
              const finalMessage: Message = {
                ...assistantMessage,
                content: finalResponseText,
                stage: 'Consultation completed',
                processingStage: undefined,
                isStreaming: false,
                userId: user?.uid || '',
                timestamp: new Date(),
                consultations: currentMsg.consultations || [],
                specialtyStatuses: currentMsg.specialtyStatuses || []
              };
              
              console.log('Final response - current consultations:', currentMsg.consultations);
              console.log('Final response - using consultations:', finalMessage.consultations);
              
              updateMessage(responseId, finalMessage);
              continue;
            }

            // Process error messages
            if (trimmedLine.startsWith('ERROR:')) {
              const [specialty, errorMessage] = trimmedLine.split('ERROR:')[1].split(':');
              throw new Error(`Error in ${specialty}: ${errorMessage}`);
            }

            // Accumulate consultation or final response text
            if (currentSpecialty && consultationText) {
              consultationText += '\n' + trimmedLine;
              // Update the consultation in real-time
              updateMessage(responseId, {
                consultations: [
                  ...(assistantMessage.consultations || []).filter(c => c.specialty !== currentSpecialty),
                  { specialty: currentSpecialty, response: consultationText }
                ]
              });
            } else if (finalResponseText) {
              finalResponseText += '\n' + trimmedLine;
              
              // Get current message state to preserve arrays
              const currentMsg = messagesRef.current.find(m => m.id === responseId);
              
              const finalMessage: Message = {
                ...assistantMessage,
                content: finalResponseText,
                stage: 'Consultation completed',
                processingStage: undefined,
                isStreaming: false,
                userId: user?.uid || '',
                timestamp: new Date(),
                // Explicitly preserve arrays from both the current state and the assistant message
                consultations: currentMsg?.consultations?.length ? currentMsg.consultations : assistantMessage.consultations || [],
                specialtyStatuses: currentMsg?.specialtyStatuses?.length ? currentMsg.specialtyStatuses : assistantMessage.specialtyStatuses || []
              };
              
              console.log('Accumulating final response - using consultations:', finalMessage.consultations);
              
              updateMessage(responseId, finalMessage);
            }
          }
        }

        // Check if response was properly completed
        if (!hasCompletedResponse) {
          // Refund credit if response wasn't completed
          await updateUserCredits(false);
          throw new Error('Response was not properly completed');
        }

        // Handle any remaining consultation
        if (currentSpecialty && consultationText) {
          updateConsultation(responseId, currentSpecialty, consultationText);
        }
      } catch (error) {
        console.error('Error:', error);
        // Improve error messages for users
        let errorMessage = 'An error occurred while processing your question. Please try again.';
        if (!navigator.onLine) {
          errorMessage = 'Please check your internet connection and try again.';
        } else if (error instanceof Error) {
          if (error.message.includes('timed out')) {
            errorMessage = 'The request took too long to complete. Please try again.';
          } else if (error.message.includes('403')) {
            errorMessage = 'Access denied. Please try logging in again.';
          } else if (error.message.includes('not properly completed')) {
            errorMessage = 'The consultation was interrupted. Your credits have been refunded. Please try again.';
          }
        }
        
        // Send error notification to admin
        await sendErrorNotification(
          `API Error: ${error instanceof Error ? error.message : 'Unknown error'} - User Message: ${input}`,
          user?.uid
        );

        // Refund the credit if there's an error
        await updateUserCredits(false);
        const errorMessageObj: Message = {
          id: Date.now().toString(),
          content: errorMessage,
          role: 'assistant',
          stage: 'Error',
          error: true,
          consultations: [],
          specialtyStatuses: [],
          conversationId: activeConversationId,
          userId: user?.uid || '',
          timestamp: new Date()
        };
        setMessages(prev => {
          const filteredMessages = prev.filter(msg => !msg.isConsulting && !msg.isStreaming);
          return [...filteredMessages, errorMessageObj];
        });
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS),
    [input, isLoading, selectedModel, credits, profileType, currentConversationId, createNewConversation, user?.uid]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    debouncedSubmit(e);
  };

  // Add session expiry handling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Verify session is still valid when tab becomes visible
        if (user?.uid) {
          const userDoc = doc(db, 'users', user.uid);
          getDoc(userDoc).catch(() => {
            // Session might be expired, redirect to login
            logout();
            navigate('/login');
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.uid, logout, navigate]);

  // Update the updateMessage function to always deeply preserve arrays
  const updateMessage = (messageId: string, updates: Partial<Message>) => {
    console.log('updateMessage called with:', { messageId, updates });
    
    // Get current message state
    const currentMsg = messagesRef.current.find(m => m.id === messageId);
    console.log('Current message state:', currentMsg);
    
    if (currentMsg) {
      console.log('Message ID:', messageId);
      console.log('Has consultations:', currentMsg.consultations?.length || 0);
      console.log('Has specialty statuses:', currentMsg.specialtyStatuses?.length || 0);
      console.log('Is streaming:', currentMsg.isStreaming);
    }
    
    setMessages(prev => {
      const messageIndex = prev.findIndex(m => m.id === messageId);
      
      if (messageIndex === -1) {
        console.warn('Message not found:', messageId);
        if (updates.role && currentConversationId) {
          const newMessage: Message = {
            id: messageId,
            content: updates.content || '',
            role: updates.role,
            isStreaming: updates.isStreaming || false,
            userId: user?.uid || '',
            conversationId: currentConversationId,
            timestamp: new Date(),
            consultations: updates.consultations || [],
            specialtyStatuses: updates.specialtyStatuses || [],
            ...updates
          };
          console.log('Creating new message:', newMessage);
          return [...prev, newMessage];
        }
        return prev;
      }

      const currentMessage = prev[messageIndex];
      
      // Create updated message with careful array handling
      const updatedMessage = {
        ...currentMessage,
        ...updates,
        // Preserve arrays from either updates or current message
        consultations: updates.consultations || currentMessage.consultations || [],
        specialtyStatuses: updates.specialtyStatuses || currentMessage.specialtyStatuses || []
      };

      console.log('Message before update:', currentMessage);
      console.log('Updated message:', updatedMessage);

      const newMessages = [...prev];
      newMessages[messageIndex] = updatedMessage;
      return newMessages;
    });
  };

  const updateConsultation = (messageId: string, specialty: string, response: string) => {
    setMessages((prevMessages) => {
      const messageIndex = prevMessages.findIndex(m => m.id === messageId);
      if (messageIndex === -1) return prevMessages;

      const message = prevMessages[messageIndex];
      const currentConsultations = message.consultations || [];
      
      // Remove any existing consultation for this specialty
      const filteredConsultations = currentConsultations.filter(c => c.specialty !== specialty);
      
      // Add new consultation
      const newConsultations = [...filteredConsultations, { specialty, response }];
      
      return prevMessages.map((msg, idx) => 
        idx === messageIndex
          ? { 
              ...msg, 
              consultations: newConsultations
            }
          : msg
      );
    });
  };

  // Update resetChat to clear conversation messages
  const resetChat = () => {
    setMessages([]);
    setInput('');
    setActiveConsultation(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Check the same conditions as the submit button
      if (!isLoading && input.trim() && credits !== null && credits > 0 && (selectedModel !== 'pro' || credits >= 15)) {
        handleSubmit(e as unknown as React.FormEvent);
      }
    }
  };

  useEffect(() => {
    // Add the styles dynamically
    const style = document.createElement('style');
    style.textContent = `
      @keyframes carousel {
        0% {
          transform: translateX(0%);
        }
        100% {
          transform: translateX(-50%);
        }
      }
      .animate-carousel {
        animation: carousel 20s linear infinite;
        display: inline-flex;
        gap: 0.5rem;
      }
      .animate-carousel > * {
        flex-shrink: 0;
      }
      @keyframes resource-change {
        0%, 20% {
          transform: translateY(0);
        }
        25%, 45% {
          transform: translateY(-24px);
        }
        50%, 70% {
          transform: translateY(-48px);
        }
        75%, 95% {
          transform: translateY(-72px);
        }
        100% {
          transform: translateY(-96px);
        }
      }
      .animate-resource-change {
        animation: resource-change 8s steps(1) infinite;
        display: flex;
        flex-direction: column;
      }
      .animate-resource-change > * {
        height: 24px;
      }
      
      /* Fix for headings in markdown content */
      .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
        color: white !important;
        font-weight: bold !important;
      }
      .prose strong {
        color: white !important;
      }
      
      /* Custom scrollbar styles */
      .scrollbar-custom {
        scrollbar-width: thin;
        scrollbar-color: #333 #1d1d1e;
      }
      
      .scrollbar-custom::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }
      
      .scrollbar-custom::-webkit-scrollbar-track {
        background: #1d1d1e;
        border-radius: 4px;
      }
      
      .scrollbar-custom::-webkit-scrollbar-thumb {
        background: #333;
        border-radius: 4px;
      }
      
      .scrollbar-custom::-webkit-scrollbar-thumb:hover {
        background: #444;
      }

      /* New animations */
      @keyframes shimmer {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }
      
      @keyframes progress {
        0% {
          width: 5%;
        }
        50% {
          width: 70%;
        }
        70% {
          width: 65%;
        }
        100% {
          width: 95%;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Profile selector component
  const ProfileSelectorModal = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm p-4"
      onClick={() => !isNewUser && setShowProfileSelector(false)}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        transition={{ duration: 0.1 }}
        className="bg-[#1A1B1E] rounded-lg shadow-lg w-full max-w-md overflow-y-auto p-6 border border-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-gray-100 mb-3">Who are you?</h2>
        <p className="text-sm text-gray-300 mb-6">
          {isNewUser 
            ? "Select your profile type to customize the health information you receive. This selection is required to continue."
            : "Select your profile type to customize the health information you receive. We'll tailor our responses based on your selection."
          }
        </p>
        
        <div className="grid grid-cols-1 gap-4 mb-6">
          <button
            onClick={() => updateUserProfile('patient')}
            className={`flex items-start gap-4 p-4 rounded-md border transition-all ${
              profileType === 'patient' 
                ? 'border-[#00B5A1] bg-[#1d3d39]' 
                : 'border-gray-800 hover:bg-[#232429] bg-[#232429]'
            }`}
          >
            <div className={`p-2 rounded-md ${profileType === 'patient' ? 'bg-[#00B5A1]' : 'bg-gray-800'}`}>
              <UserRound className={`w-6 h-6 ${profileType === 'patient' ? 'text-white' : 'text-gray-300'}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-medium text-gray-100">I'm a Patient</h3>
                {profileType === 'patient' && <CheckCircle className="w-5 h-5 text-[#00B5A1]" />}
              </div>
              <p className="mt-1 text-sm text-gray-300">
                Get health information explained in easy to understand language, with practical advice for daily life.
              </p>
            </div>
          </button>
          
          <button
            onClick={() => updateUserProfile('professional')}
            className={`flex items-start gap-4 p-4 rounded-md transition-all ${
              profileType === 'professional' 
                ? 'bg-[#1d3d39]' 
                : 'hover:bg-[#232429] bg-[#232429]'
            }`}
          >
            <div className={`p-2 rounded-md ${profileType === 'professional' ? 'bg-[#00B5A1]' : 'bg-gray-800'}`}>
              <UserCog className={`w-6 h-6 ${profileType === 'professional' ? 'text-white' : 'text-gray-300'}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-medium text-gray-100">I'm a Healthcare Professional</h3>
                {profileType === 'professional' && <CheckCircle className="w-5 h-5 text-[#00B5A1]" />}
              </div>
              <p className="mt-1 text-sm text-gray-300">
                Receive detailed technical information with clinical terminology, research references, and evidence-based practice guidelines.
              </p>
            </div>
          </button>
        </div>
        
        <div className="flex justify-end gap-3">
          {/* Only show cancel button for existing users who are changing their profile */}
          {!isNewUser && (
            <button
              onClick={() => setShowProfileSelector(false)}
              className="px-4 py-2 text-gray-400 hover:text-gray-200 text-sm"
            >
              Cancel
            </button>
          )}
          <button
            onClick={() => updateUserProfile(profileType)}
            className="px-4 py-2 bg-[#00B5A1] text-white rounded-md hover:bg-[#00a090] ml-2 text-sm"
          >
            Confirm
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  // Update the submitFeedback function to work with the new feedbackState
  const submitFeedback = async (messageId: string, feedback: MessageFeedback) => {
    if (!user?.uid) return;
    
    try {
      // Find the message in the current conversation
      const message = messages.find(m => m.id === messageId);
      if (!message) return;
      
      // Update the message in the state
      updateMessage(messageId, { feedback });
      
      // Timestamp for the feedback submission
      const timestamp = new Date();
      
      // Instead of updating the message document, create a new document in a feedback collection
      const feedbackCollection = collection(db, 'feedback');
      
      // Create the feedback document
      const feedbackDoc = {
        messageId: messageId,
        conversationId: message.conversationId,
        userId: user.uid,
        userEmail: user.email || '',
        rating: feedback.rating,
        feedbackMessage: feedback.message,
        timestamp: timestamp,
        // Additional context to make feedback more useful
        model: message.model || 'mini',
        profileType: profileType,
        finalResponse: message.content
      };
      
      console.log("Saving feedback to Firestore:", feedbackDoc);
      
      // Add document to the feedback collection
      await addDoc(feedbackCollection, feedbackDoc)
        .then(docRef => {
          console.log("Feedback saved with ID:", docRef.id);
          
          // Log the feedback event in analytics
          trackEvent(
            feedback.rating === 'like' ? AnalyticsEvents.RESPONSE_LIKED : AnalyticsEvents.RESPONSE_DISLIKED,
            {
              messageId,
              conversationId: message.conversationId,
              hasFeedbackMessage: feedback.message.length > 0,
              feedbackRating: feedback.rating
            }
          );
        })
        .catch(error => {
          console.error("Error saving feedback:", error);
        });
      
    } catch (error) {
      console.error('Error saving feedback:', error);
    }
  };

  // When the API responds with specialties, update the message
  const processSpecialties = (messageId: string, specialtiesText: string) => {
    const specialties = specialtiesText.split(',').map(s => s.trim());
    console.log("Processing specialties:", specialties);
    
    // Create initial specialtyStatuses with pending/consulting states
    const firstSpecialty = specialties[0];
    const specialtyStatuses = specialties.map(specialty => ({
      specialty,
      status: specialty === firstSpecialty ? 'consulting' as const : 'pending' as const
    }));
    
    // Update the message with these specialties
    updateMessage(messageId, {
      specialtyStatuses,
      processingStage: `Loading ${firstSpecialty} medical database...`,
      stage: 'Specialist Consultation'
    });
  };

  // Process specialty status update
  const processSpecialtyStatus = (messageId: string, specialty: string, status: SpecialtyStatus['status']) => {
    const currentMessage = messages.find(m => m.id === messageId);
    if (!currentMessage || !currentMessage.specialtyStatuses) return;
    
    const updatedStatuses = currentMessage.specialtyStatuses.map(s => 
      s.specialty === specialty ? { ...s, status } : s
    );
    
    // Update the message
    updateMessage(messageId, {
      specialtyStatuses: updatedStatuses
    });
  };

  // Function to toggle sidebar
  const toggleSidebar = () => {
    trackClarityEvent('sidebar_toggle', { action: showSidebar ? 'close' : 'open' });
    setShowSidebar(!showSidebar);
  };
  
  return (
    <div className="flex h-screen bg-[#0D0F10] text-gray-100">
      {/* Mobile sidebar toggle */}
      <button
        onClick={toggleSidebar}
        className="md:hidden fixed top-2 left-2 z-30 p-1.5 bg-[#1d1d1e] text-gray-300 rounded-full shadow-md hover:bg-[#252525] border border-gray-700"
        aria-label={showSidebar ? "Close sidebar" : "Open sidebar"}
      >
        {showSidebar ? <X size={18} /> : <MenuIcon size={18} />}
      </button>
      
      {/* Chat sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <>
            {/* Backdrop overlay to close sidebar */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-10 md:hidden"
              onClick={() => setShowSidebar(false)}
            />
            <motion.div
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ duration: 0.2 }}
              className="fixed md:static inset-y-0 left-0 z-20 md:z-auto md:w-[260px] flex-shrink-0 h-full shadow-md"
            >
              {/* Separate close button positioned better for mobile */}
              <button
                onClick={() => setShowSidebar(false)}
                className="md:hidden absolute top-3 right-3 z-30 p-2 bg-[#232429] text-gray-300 rounded-full shadow-md hover:bg-[#2a2b31]"
                aria-label="Close sidebar"
              >
                <X size={20} />
              </button>
              
              <ChatSidebar />
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      {/* Main chat area */}
      <div className="flex-1 flex flex-col h-full overflow-hidden relative bg-[#151415]">
        {/* Header */}
        <div className="h-14 border-b border-gray-800 flex items-center justify-between px-2 sm:px-4 flex-shrink-0 bg-[#151415] z-10">
          <div className="flex items-center">
            {/* Logo placement - added margin-left on mobile */}
            <div className="md:ml-0 ml-10">
              <img 
                src="/logo.png" 
                alt="Yesil Health Logo" 
                className="h-7 w-7 sm:h-8 sm:w-8 mr-1 sm:mr-2 object-contain" 
                loading="lazy" 
                width="32" 
                height="32" 
              />
            </div>
            <h1 className="text-sm sm:text-base font-medium text-gray-100 hidden xs:block">Yesil Health AI</h1>
            {credits !== null && (
              <Link
                to="/pricing"
                className="ml-2 sm:ml-3 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-[#1d3d39] border border-[#1a3733] text-[#00B5A1] text-xs font-medium flex items-center gap-1 sm:gap-1.5 hover:bg-[#1a3733] transition-colors cursor-pointer"
                title="Click to buy more credits"
              >
                <CreditCard size={12} />
                <span className="hidden xs:inline">{credits} credits</span>
                <span className="xs:hidden">{credits}</span>
              </Link>
            )}
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
              {/* Profile Type Selector */}
              <button
                onClick={() => setShowProfileSelector(true)}
                className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-gray-300 hover:text-gray-100 rounded-full hover:bg-[#1d1d1e] ${profileType === 'professional' ? '' : 'border border-gray-700'}`}
              >
                <UserCog size={14} />
                <span className="text-xs hidden xs:inline">{profileType === 'patient' ? 'Patient' : 'Professional'}</span>
              </button>
              
              {/* Research Button */}
              <button
                onClick={() => window.open('https://s3.ca-central-1.amazonaws.com/assets.jmir.org/assets/preprints/preprint-72646-submitted.pdf', '_blank')}
                className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-gray-300 hover:text-gray-100 rounded-full hover:bg-[#1d1d1e] border border-gray-700"
                title="View Research Paper"
              >
                <FileText size={14} />
                <span className="text-xs hidden xs:inline">Research</span>
              </button>
              
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center p-1.5 sm:p-2 text-gray-300 hover:text-gray-100 rounded-full hover:bg-[#1d1d1e]">
                  <div className="h-6 w-6 rounded-full overflow-hidden bg-gray-600 flex items-center justify-center text-gray-100 text-sm">
                    {user?.photoURL ? (
                      <img 
                        src={user.photoURL} 
                        alt="User" 
                        className="h-full w-full object-cover" 
                        loading="lazy"
                        onError={(e) => {
                          // If image fails to load, show first letter of name
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = user?.displayName?.charAt(0) || 'U';
                        }}
                      />
                    ) : (
                      <span>{user?.displayName?.charAt(0) || 'U'}</span>
                    )}
                  </div>
                  <ChevronDown size={14} className="ml-1 hidden sm:block" />
                </Menu.Button>
                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                <Menu.Items className="absolute right-0 mt-2 w-48 bg-[#2A2B32] rounded-md shadow-lg py-1 z-50 border border-gray-800">
                      <Menu.Item>
                        {({ active }) => (
                      <Link
                        to="/pricing"
                            className={`${
                          active ? 'bg-gray-800' : ''
                        } flex w-full items-center px-4 py-2 text-left text-sm text-gray-300`}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Buy Credits
                      </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                      <Link
                        to="/dashboard/api"
                            className={`${
                          active ? 'bg-gray-800' : ''
                        } flex w-full items-center px-4 py-2 text-left text-sm text-gray-300`}
                      >
                        <Key className="w-4 h-4 mr-2" />
                        API Dashboard
                      </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                      <a
                        href="mailto:support@yesilhealth.ai"
                        className={`${
                          active ? 'bg-gray-800' : ''
                        } flex w-full items-center px-4 py-2 text-left text-sm text-gray-300`}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Contact Us
                      </a>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={logout}
                        className={`${
                          active ? 'bg-gray-800' : ''
                        } flex w-full items-center px-4 py-2 text-left text-sm text-gray-300`}
                          >
                        <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                          </button>
                        )}
                      </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
        </div>
      </div>

      {/* Welcome Banner */}
      <AnimatePresence>
        {showWelcomeBanner && (
          <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-2 sm:p-3 mx-2 sm:mx-4 mt-2 sm:mt-3 bg-[#1d3d39] rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm sm:text-base font-medium text-gray-100">Welcome to Yesil Health AI</h2>
                  <p className="text-[10px] sm:text-xs text-gray-300 mt-0.5 sm:mt-1">
                    Ask any health-related questions and get evidence-based insights from multiple medical specialties.
                  </p>
                </div>
                <button
                  onClick={() => setShowWelcomeBanner(false)}
                  className="text-gray-400 hover:text-gray-200 rounded-full p-0.5 sm:p-1 hover:bg-[#1a3733] flex-shrink-0 ml-1"
                >
                  <X size={16} />
                </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto py-3 sm:py-4 px-2 sm:px-3 md:px-4 pb-32 sm:pb-24 md:pb-4" style={{ scrollBehavior: 'smooth' }}>
          {messages.length === 0 && !showWelcomeBanner ? (
            <div className="h-full flex items-center justify-center flex-col">
              <div className="bg-[#1d3d39] p-2.5 sm:p-3 rounded-full mb-3 sm:mb-4">
                <MessageCircleQuestion size={24} className="text-[#00B5A1]" />
              </div>
              <h3 className="text-base sm:text-lg font-medium text-gray-200 mb-2">How can I help you?</h3>
              <p className="text-xs sm:text-sm text-gray-400 text-center max-w-md mb-5 sm:mb-6 px-2">
                Ask any health-related question and I'll provide evidence-based insights from multiple medical specialties.
              </p>
              
              {/* Quick questions section */}
              <div className="w-full max-w-lg mx-auto mb-6 sm:mb-8 px-2 sm:px-0">
                <h4 className="text-[10px] sm:text-xs font-medium text-gray-300 mb-2">Try asking about:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 sm:gap-2">
                  <button
                    onClick={() => setInput("What are the best ways to lower blood pressure naturally?")}
                    className="text-left p-1.5 sm:p-2.5 rounded-lg border border-gray-700 bg-[#1d1d1e] hover:bg-[#252525] transition-colors text-[10px] sm:text-xs text-gray-300"
                  >
                    Ways to lower blood pressure naturally
                  </button>
                  <button
                    onClick={() => setInput("What are common symptoms of vitamin D deficiency?")}
                    className="text-left p-1.5 sm:p-2.5 rounded-lg border border-gray-700 bg-[#1d1d1e] hover:bg-[#252525] transition-colors text-[10px] sm:text-xs text-gray-300"
                  >
                    Symptoms of vitamin D deficiency
                  </button>
                  <button
                    onClick={() => setInput("How can I improve my sleep quality?")}
                    className="text-left p-1.5 sm:p-2.5 rounded-lg border border-gray-700 bg-[#1d1d1e] hover:bg-[#252525] transition-colors text-[10px] sm:text-xs text-gray-300"
                  >
                    Improving sleep quality
                  </button>
                  <button
                    onClick={() => setInput("What are the most effective exercises for lower back pain?")}
                    className="text-left p-1.5 sm:p-2.5 rounded-lg border border-gray-700 bg-[#1d1d1e] hover:bg-[#252525] transition-colors text-[10px] sm:text-xs text-gray-300"
                  >
                    Exercises for lower back pain
                  </button>
                </div>
              </div>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={`${message.id}-${index}`}
                className={`px-2 sm:px-4 py-4 sm:py-6 group ${
                  message.role === 'assistant' 
                    ? 'bg-[#151415]' 
                    : 'bg-[#151415]'
                }`}
              >
                {message.role === 'assistant' && (() => {
                  // Debug info
                  console.log("Message ID:", message.id);
                  console.log("Has consultations:", message.consultations ? message.consultations.length : 0);
                  console.log("Has specialty statuses:", message.specialtyStatuses ? message.specialtyStatuses.length : 0);
                  console.log("Is streaming:", message.isStreaming);
                  return null;
                })()}
                <div className="max-w-3xl mx-auto">
                  <div className={`flex items-start`}>
                    {message.role === 'assistant' && (
                      <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-sm flex items-center justify-center flex-shrink-0 mr-3 sm:mr-4 mt-0.5 overflow-hidden">
                        <img src="/logo.png" alt="Yesil AI" className="h-7 w-7 sm:h-8 sm:w-8 object-contain" />
                      </div>
                    )}
                    
                    <div className="flex-1">
                      {/* Specialty widgets container - show if either consultations, specialtyStatuses, or message is consulting */}
                      {message.role === 'assistant' && 
                        ((message.consultations && message.consultations.length > 0) || 
                         message.specialtyStatuses?.some(status => 
                           status.status === 'pending' || status.status === 'consulting') ||
                         message.isConsulting) && (
                        <div className="specialty-container mb-3 border border-[#1a3733] bg-[#121212] rounded-md shadow-sm">
                          {/* Completed consultations section */}
                          {message.consultations && message.consultations.length > 0 && (
                            <div className="p-2">
                              <div className="mb-1 text-xs text-[#00B5A1] font-medium flex items-center gap-1">
                                <Database size={12} />
                                <span>Specialty Insights ({message.consultations.length})</span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {message.consultations.map((consultation) => (
                                  <div
                                    key={consultation.specialty}
                                    className="p-2 rounded-md border border-[#1a3733] bg-gradient-to-r from-[#1d3d39]/50 to-[#1A1B1E] cursor-pointer hover:from-[#1d3d39] hover:to-[#1d3d39]/70 transition-colors duration-200 flex-1 min-w-[140px] xs:min-w-[180px] max-w-[200px] xs:max-w-[250px]"
                                    onClick={() => setActiveConsultation(consultation)}
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="flex items-center gap-1 xs:gap-1.5">
                                        {getSpecialtyIcon(consultation.specialty, 14)}
                                        <span className="text-[10px] xs:text-xs font-medium text-gray-200">{consultation.specialty}</span>
                                      </div>
                                      <div className="flex items-center gap-1 bg-[#1d3d39] border border-[#1a3733] px-1 xs:px-1.5 py-0.5 rounded-full">
                                        <CheckCircle size={10} className="text-[#00B5A1]" />
                                        <span className="text-[8px] xs:text-[9px] font-medium text-[#00B5A1]">Complete</span>
                                      </div>
                                    </div>
                                    <div className="text-[10px] xs:text-[11px] text-gray-400 line-clamp-1 ml-4 xs:ml-5">
                                      {consultation.response.substring(0, 80).replace(/^#+\s+/gm, '')}...
                                    </div>
                                    <div className="flex justify-end mt-1">
                                      <button className="text-[9px] xs:text-[10px] text-[#00B5A1] hover:text-[#00c2ac] flex items-center gap-0.5">
                                        <span>View report</span>
                                        <ChevronDown size={10} className="transform -rotate-90" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Show processing section only when there are no consultations yet */}
                          {!message.consultations?.length && ((message.specialtyStatuses?.some(status => 
                            (status.status === 'pending' || status.status === 'consulting'))) || 
                            message.isConsulting) && (
                            <div className="p-2">
                              <div className="w-full mb-1 text-xs text-gray-300 font-medium">Processing:</div>
                              <div className="flex flex-wrap gap-2">
                                {message.specialtyStatuses
                                  ?.filter(status => 
                                    status.status === 'pending' || status.status === 'consulting'
                                  )
                                  .map((status) => (
                                    <div
                                      key={status.specialty}
                                      className={`flex items-center gap-0.5 xs:gap-1 text-[10px] xs:text-xs py-1 xs:py-1.5 px-2 xs:px-3 rounded-md shadow-sm
                                        ${status.status === 'consulting' 
                                          ? 'bg-[#232329] text-gray-300 border border-gray-700' 
                                          : 'bg-[#1d1d1e] text-gray-400 border border-gray-700'}`}
                                    >
                                      {getSpecialtyIcon(status.specialty, 12)}
                                      <span className="font-medium">{status.specialty}</span>
                                      {status.status === 'consulting' ? (
                                        <div className="animate-spin h-2.5 w-2.5 xs:h-3 xs:w-3 border-2 border-current border-t-transparent rounded-full ml-1" />
                                      ) : (
                                        <Clock size={10} className="ml-1" />
                                      )}
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {message.role === 'user' ? (
                        <div className="flex justify-end">
                          <p className="whitespace-pre-wrap text-gray-200 text-[15px] sm:text-[16px] bg-[#252525] px-3 sm:px-4 py-2 sm:py-3 rounded-3xl max-w-full inline-block">{message.content}</p>
                      </div>
                      ) : message.isConsulting ? (
                        <div>
                          <div className="flex items-center">
                            <span className="mr-2 text-gray-200 text-[15px] sm:text-[16px]">{message.content}</span>
                            <div className="ml-2 flex-shrink-0">
                              <div className="animate-spin h-3 sm:h-4 w-3 sm:w-4 border-2 border-[#00B5A1] border-t-transparent rounded-full"></div>
                            </div>
                          </div>
                          {message.stage && (
                            <div className="text-[12px] sm:text-[12px] text-gray-400 mt-1">
                              {message.stage}
                            </div>
                          )}
                        </div>
                      ) : message.isStreaming ? (
                        <div>
                          {message.content ? (
                            <div className="prose prose-sm max-w-none prose-invert">
                              <ReactMarkdown
                                components={{
                                  p: ({node, ...props}) => <p className="mb-4 last:mb-0 text-gray-200 text-[15px] sm:text-[16px]" {...props} />,
                                  h1: ({node, ...props}) => <h1 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 mt-5 sm:mt-6 text-white" {...props} />,
                                  h2: ({node, ...props}) => <h2 className="text-sm sm:text-base font-bold mb-2 sm:mb-3 mt-4 sm:mt-5 text-white" {...props} />,
                                  h3: ({node, ...props}) => <h3 className="text-xs sm:text-sm font-bold mb-1 sm:mb-2 mt-3 sm:mt-4 text-white" {...props} />,
                                  h4: ({node, ...props}) => <h4 className="text-xs font-bold mb-1 sm:mb-2 mt-2 sm:mt-3 text-white" {...props} />,
                                  ul: ({node, ...props}) => <ul className="list-disc pl-4 sm:pl-6 mb-3 sm:mb-4 space-y-1 sm:space-y-2 text-[15px] sm:text-[16px]" {...props} />,
                                  ol: ({node, ...props}) => <ol className="list-decimal pl-4 sm:pl-6 mb-3 sm:mb-4 space-y-1 sm:space-y-2 text-[15px] sm:text-[16px]" {...props} />,
                                  li: ({node, ...props}) => <li className="mb-1 text-gray-200 text-[15px] sm:text-[16px]" {...props} />,
                                  strong: ({node, ...props}) => <strong className="font-semibold text-white" {...props} />
                                }}
                              >
                                {message.content}
                              </ReactMarkdown>
                            </div>
                          ) : null}
                          
                          {message.processingStage && (
                            <div className="mt-2 bg-[#121212] border border-gray-800 rounded-md p-2">
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  <div className="animate-spin h-3 w-3 border-2 border-[#00B5A1] border-t-transparent rounded-full"></div>
                                  <span className="text-xs font-medium text-[#00B5A1]">Processing</span>
                                </div>
                                <span className="text-xs text-gray-500">{Math.floor(Math.random() * 100) + 1}%</span>
                              </div>
                              <div className="text-xs text-gray-400 mb-1.5">{message.processingStage}</div>
                              <div className="h-1 bg-[#232329] rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#007a6e] to-[#00B5A1] animate-pulse" 
                                  style={{
                                    width: `${Math.floor(Math.random() * 60) + 40}%`,
                                  }}
                                ></div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : message.error ? (
                        <div className="text-red-400 text-[13px] sm:text-[13px]">
                          <p>{message.content}</p>
                        </div>
                      ) : (
                        <div>
                          <div className="prose prose-sm max-w-none prose-invert">
                            <ReactMarkdown
                              components={{
                                p: ({node, ...props}) => <p className="mb-4 last:mb-0 text-gray-200 text-[15px] sm:text-[16px]" {...props} />,
                                ul: ({node, ...props}) => <ul className="list-disc pl-4 sm:pl-6 mb-3 sm:mb-4 space-y-1 sm:space-y-2 text-[15px] sm:text-[16px]" {...props} />,
                                ol: ({node, ...props}) => <ol className="list-decimal pl-4 sm:pl-6 mb-3 sm:mb-4 space-y-1 sm:space-y-2 text-[15px] sm:text-[16px]" {...props} />,
                                li: ({node, ...props}) => <li className="mb-1 text-gray-200 text-[15px] sm:text-[16px]" {...props} />,
                                strong: ({node, ...props}) => <strong className="font-semibold text-white" {...props} />,
                              }}
                            >
                              {message.content}
                            </ReactMarkdown>
                             
                            {message.role === 'assistant' && !message.isStreaming && !message.error && (
                              <>
                                {/* Evidence-based footer after content - only for health messages */}
                                {!message.isNonHealth && (
                                  <>
                                    <div className="mt-5 pt-3 border-t border-gray-800 flex items-center gap-1 sm:gap-2">
                                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#00B5A1]">
                                        <path fillRule="evenodd" d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813A3.75 3.75 0 007.466 7.89l.813-2.846A.75.75 0 019 4.5z" clipRule="evenodd" />
                                      </svg>
                                      <span className="text-[9px] xs:text-[10px] sm:text-[11px] text-gray-400">This evidence-based response was generated using medical textbooks, peer-reviewed studies, and clinical guidelines.</span>
                                    </div>
                                    
                                    {/* Separate disclaimer */}
                                    <div className="mt-1.5 sm:mt-2 pl-4 sm:pl-6">
                                      <span className="text-[8px] xs:text-[9px] sm:text-[10px] text-gray-500">Disclaimer: This information is for educational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified health provider with any questions regarding your health. Do not disregard professional medical advice because of something you have read here.</span>
                                    </div>
                                  </>
                                )}
                                
                                <div className="flex items-center mt-3 space-x-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <MessageFeedbackComponent 
                                    messageId={message.id} 
                                    onSubmitFeedback={submitFeedback}
                                    setFeedbackState={setFeedbackState}
                                  />
                                  
                                  {/* Model indicator */}
                                  {message.model && (
                                    <div className="flex items-center gap-1.5 text-gray-400">
                                      <span className="w-1 h-1 bg-gray-500 rounded-full"></span>
                                      <div className="flex items-center gap-1 text-[10px]">
                                        {message.model === 'pro' ? (
                                          <>
                                            <span>Yesil o1 PRO</span>
                                          </>
                                        ) : (
                                          <>
                                            <span>Yesil o1 mini</span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Chat input area with model selector on the left */}
        <div className={`px-2 sm:px-3 md:px-4 py-2 sm:py-3 border-t border-gray-800 fixed sm:relative bottom-0 left-0 right-0 bg-[#151415] z-20`}>
          {/* Center container with max width */}
          <div className="max-w-3xl mx-auto">
            <div className="flex gap-1 sm:gap-2 items-center bg-[#1d1d1e] rounded-xl border border-gray-700 p-1.5 sm:p-2">
              {/* Model selector */}
              <Menu as="div" className="relative">
                <Menu.Button className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-[#252525] transition-colors ${
                    selectedModel === 'mini'
                    ? 'text-[#00B5A1]' 
                    : 'text-gray-400'
                  }`}
                >
                  {selectedModel === 'mini' ? (
                    <>
                      <MessageCircleQuestion size={14} className="text-[#00B5A1]" /> 
                      <span className="text-xs font-medium hidden xs:inline">mini</span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} className="text-[#00B5A1]" /> 
                      <span className="text-xs font-medium hidden xs:inline">PRO</span>
                    </>
                  )}
                  <ChevronDown size={12} className="hidden xs:block" />
                </Menu.Button>
                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-0 bottom-11 w-56 bg-[#1d1d1e] rounded-md shadow-lg py-1 z-50 border border-gray-800">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={(e) => handleModelSelect(e, 'mini')}
                          className={`${
                            active ? 'bg-[#1d3d39]' : ''
                          } ${selectedModel === 'mini' ? 'text-[#00B5A1] bg-[#132725]' : 'text-gray-300'}
                            flex w-full items-center px-4 py-2 text-left text-sm`}
                        >
                          <MessageCircleQuestion size={16} className="mr-2" />
                          <div className="flex flex-col items-start">
                            <span className="font-medium">Yesil o1 mini</span>
                            <span className="text-xs opacity-80">1 credit per message</span>
                          </div>
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={(e) => handleModelSelect(e, 'pro')}
                          className={`${
                            active ? 'bg-[#1d3d39]' : ''
                          } ${selectedModel === 'pro' ? 'text-[#00B5A1] bg-[#132725]' : 'text-gray-300'}
                            flex w-full items-center px-4 py-2 text-left text-sm`}
                        >
                          <Sparkles size={16} className="mr-2" />
                          <div className="flex flex-col items-start">
                            <span className="font-medium">Yesil o1 PRO</span>
                            <span className="text-xs opacity-80">15 credits per message</span>
                          </div>
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>

              {/* Divider */}
              <div className="h-7 w-px bg-gray-700"></div>

              {/* Chat input field */}
              <form onSubmit={handleSubmit} className="flex-1 flex items-center gap-1 sm:gap-2 relative">
                {(credits === null || credits === 0 || (selectedModel === 'pro' && credits < 15)) && (
                  <div className="absolute -top-9 left-0 right-0 py-1.5 px-3 bg-[#3d1d1d] border border-red-800 text-xs flex items-center justify-between rounded-t-md">
                    <div className="flex items-center gap-2">
                      <AlertCircle size={12} className="text-red-400 flex-shrink-0" />
                      <span className="text-gray-300">
                        {credits === null || credits === 0
                          ? "No credits remaining" 
                          : "Insufficient credits for PRO model (requires 15 credits)"}
                      </span>
                    </div>
                    <Link to="/pricing" className="text-[#00B5A1] hover:text-[#00c2ac] flex items-center gap-1">
                      <span>Buy credits</span>
                      <ChevronDown size={12} className="transform -rotate-90" />
                    </Link>
                  </div>
                )}
                <div className="relative flex-1">
                  <textarea
                    className="w-full resize-none bg-transparent focus:outline-none text-gray-100 px-2 scrollbar-custom"
                    placeholder="Your health question..."
                    rows={1}
                    style={{ 
                      height: inputHeight, 
                      minHeight: '36px',
                      maxHeight: '140px',
                      overflowY: Number(inputHeight.replace('px', '')) > 36 ? 'auto' : 'hidden',
                      lineHeight: '18px',
                      paddingTop: '9px',
                      paddingBottom: '9px'
                    }}
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      adjustInputHeight(e);
                    }}
                    onKeyDown={handleKeyPress}
                    disabled={isLoading}
                  />
                </div>

                <div className="flex items-center gap-1 sm:gap-2 pr-1 sm:pr-2">
                  <button
                    type="button"
                    onClick={resetChat}
                    className="p-1 sm:p-2 text-gray-400 hover:text-gray-300 rounded-full hover:bg-[#252525] transition-colors"
                    title="Clear chat"
                  >
                    <Trash2 size={16} />
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !input.trim() || credits === null || credits === 0 || (selectedModel === 'pro' && credits < 15)}
                    className={`p-1 sm:p-2 text-[#00B5A1] rounded-full hover:bg-[#252525] transition-colors flex items-center justify-center ${
                      isLoading || !input.trim() || credits === null || credits === 0 || (selectedModel === 'pro' && credits < 15) ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    title={
                      credits === null || credits === 0
                        ? "You have 0 credits remaining. Please purchase more credits." 
                        : selectedModel === 'pro' && credits < 15
                        ? "Insufficient credits for PRO model (requires 15 credits)"
                        : ""
                    }
                  >
                    {isLoading ? (
                      <div className="animate-spin h-4 w-4 border-2 border-[#00B5A1] border-t-transparent rounded-full"></div>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          <div className="text-center text-[10px] sm:text-[11px] text-gray-500 opacity-70 mt-1 sm:mt-2 max-w-3xl mx-auto">
            <span>Information provided is for educational purposes only. Always verify important medical information.</span>
          </div>
        </div>
      </div>

      {/* Credits update popup */}
      <Transition
        show={showCreditUpdatePopup}
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            className="bg-gray-800 rounded-xl shadow-lg p-6 max-w-md w-full mx-4 border border-gray-700"
          >
            <div className="flex items-center justify-center w-16 h-16 bg-[#1d3d39] text-[#00B5A1] rounded-full mx-auto mb-4">
              <Sparkles className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-semibold text-center text-gray-100 mb-2">Credits Added!</h3>
            <p className="text-center text-gray-300 mb-6">
              Your purchase was successful. Your account has been credited with additional consultations.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => setShowCreditUpdatePopup(false)}
                className="px-4 py-2 bg-[#00B5A1] text-white rounded-lg hover:bg-[#00a090] transition-colors"
              >
                Continue
              </button>
            </div>
          </motion.div>
        </div>
      </Transition>
      
      {/* Consultation popup */}
      <AnimatePresence>
        {activeConsultation && (
          <ConsultationPopup
            consultation={activeConsultation}
            onClose={() => setActiveConsultation(null)}
          />
        )}
      </AnimatePresence>

      {/* Profile selector modal */}
      {showProfileSelector && <ProfileSelectorModal />}
      
      {/* Feedback modal */}
      {feedbackState.showModal && (
        <FeedbackModal
          isOpen={feedbackState.showModal}
          onClose={() => setFeedbackState({ ...feedbackState, showModal: false })}
          onSubmit={feedbackState.handleSubmit}
          rating={feedbackState.rating}
        />
      )}
    </div>
  );
}; 